import styled from '@emotion/styled'
import Cancel from '@mui/icons-material/Cancel'
import Close from '@mui/icons-material/Close'
import ExpandMore from '@mui/icons-material/ExpandMore'
import InfoIcon from '@mui/icons-material/Info'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {
  closeSnackbar,
  enqueueSnackbar as enqueueSnackbarOutsideComponent,
  type OptionsObject,
  SnackbarContent,
  type SnackbarKey,
  type SnackbarMessage,
  useSnackbar,
  type VariantType,
} from 'notistack'
import { forwardRef, type Ref, useCallback, useState } from 'react'
import { LOGIN_PATH } from 'routes/constants'
import { colors } from 'themes'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import Spacings from '../figma/tokens/Spacings'
import { captureAxiosError } from '../helpers/sentryHelpers'
import FigmaBox from '../mynt-components/components/FigmaBox'

type ErrorWithDetails = { response?: { data?: { detail?: string } } }

type NotificationProps = {
  message: SnackbarMessage
  snackbarKey?: SnackbarKey
  error?: ErrorWithDetails
  variant?: string
}

const getVariantColor = (variant: string | undefined) => {
  switch (variant) {
    case 'error':
      return {
        text: 'white',
        background: colors.product.error[600],
      }
    case 'success':
      return {
        text: 'white',
        background: colors.product.success[600],
      }
    case 'warning':
      return {
        text: 'white',
        background: colors.product.alert[600],
      }
    default:
      return {
        text: 'black',
        background: 'white',
      }
  }
}

export const Notification = forwardRef(function Notification(
  { error, snackbarKey, message, variant }: NotificationProps,
  ref: Ref<HTMLDivElement>,
) {
  const [expanded, setExpanded] = useState(false)

  const handleExpandClick = useCallback(() => {
    setExpanded((oldExpanded) => !oldExpanded)
  }, [])

  const dismissMaterialNotification = () => {
    closeSnackbar(snackbarKey)
  }

  const detail = error?.response?.data?.detail
  const variantColors = getVariantColor(variant)

  const Icon = variant === 'error' ? Cancel : InfoIcon

  return (
    <SnackbarContent ref={ref}>
      <FullWidthCardWrapper background={variantColors.background} textColor={variantColors.text}>
        <CardActionsWrapper>
          <Box sx={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Icon fontSize="small" style={{ marginRight: 8, color: variantColors.text }} />
              <Typography
                sx={{
                  color: variantColors.text,
                  fontSize: 14,
                  fontFamily: 'Inter',
                  fontWeight: 500,
                }}
              >
                {message}
              </Typography>
            </Box>
            <FigmaBox style={{ marginLeft: 'auto', marginRight: -8, alignItems: 'center' }} direction="row">
              {detail && (
                <IconButtonRotate size="small" rotate={expanded} onClick={handleExpandClick}>
                  <ExpandMore />
                </IconButtonRotate>
              )}
              <IconButton size="small" onClick={dismissMaterialNotification}>
                <Close fontSize="small" />
              </IconButton>
            </FigmaBox>
          </Box>
        </CardActionsWrapper>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <FullscreenPaper>{detail}</FullscreenPaper>
        </Collapse>
      </FullWidthCardWrapper>
    </SnackbarContent>
  )
})

const IconButtonRotate = styled(IconButton)<{ rotate?: boolean }>`
  transition: transform 200ms;
  padding: 2px;
  transform: rotate(${({ rotate }) => (rotate ? '180' : '0')}deg);
`

const FullscreenPaper = styled(Paper)`
  padding: ${Spacings.small};
  overflow-y: scroll;
  max-height: 80vh;
`

const CardActionsWrapper = styled(CardActions)`
  padding: 6px 16px 6px 20px;
`

const FullWidthCardWrapper = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'background' && prop !== 'textColor',
})<{ background?: string; textColor?: string }>`
  width: 100%;
  background-color: ${({ background }) => background || ColorsDeprecated.baseWhite};
  color: ${({ textColor }) => textColor || 'white'};
  font-family: Inter;
`

const isSignedOutError = (code: number, messages: string[]) => {
  if (code !== 401) return false

  const matchingStrings = ['not authorized', 'support@mynt.se']

  return messages.some((message) => matchingStrings.some((matchingString) => message.toLowerCase().includes(matchingString)))
}

export const MaterialNotificationAxios = async (error: any) => {
  const errorCode = error?.response?.status
  const title = error?.response?.data?.title
  const message = error?.response?.data?.message
  const detail = error?.response?.data?.detail
  const traceId = error?.response?.headers?.['x-mynt-trace-id']

  if (error?.response?.config?.disableHandler || error.config?.disableCustomErrorHandler) {
    return Promise.reject(error)
  }

  // if (isSignedOutError(errorCode, [title ?? message, detail])) {
  //   window.location.href = LOGIN_PATH
  // }

  if (errorCode === 400 || errorCode >= 404 || errorCode >= 500) captureAxiosError(error)

  const shortMessage = title || message || 'Something Went Wrong'

  materialNotifyOutsideComponent(traceId ? `${shortMessage} (trace ID: ${traceId})` : shortMessage, 'error', {
    content: (key, message) => <Notification variant="error" message={message} snackbarKey={key} error={error} />,
    autoHideDuration: 60 * 1000,
  })

  return Promise.reject(error)
}

export const materialNotifyOutsideComponent = (message: string, variant: VariantType = 'success', options?: OptionsObject) => {
  enqueueSnackbarOutsideComponent(message, {
    variant: variant,
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
    autoHideDuration: 5000,
    ...options,
  })
}

const useMaterialNotification = () => {
  const { enqueueSnackbar } = useSnackbar()

  return (message: string, variant: VariantType = 'success', options?: OptionsObject) =>
    enqueueSnackbar(message, {
      variant: variant,
      anchorOrigin: { horizontal: 'right', vertical: 'top' },
      content: (key, message) => <Notification variant={variant} message={message} snackbarKey={key} />,
      autoHideDuration: 5000,
      ...options,
    })
}

export default useMaterialNotification
